import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { formatFranc } from '../lib/utils';
import { useAds } from '../contexts/AdContext';
import { entityAPI, teamAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Crown, TrendingUp, TrendingDown, MapPin, Users, BarChart3, Star, Plus, Minus, Newspaper, Lock, Eye, EyeOff, ExternalLink, HelpCircle, Sparkles } from 'lucide-react';
import FrancIcon from '../components/FrancIcon';
import { AffiliationDisclaimer } from '../components/LegalDisclaimer';
import { toast } from 'sonner';
// v6.x audit P2 — recharts is code-split out of the main bundle: the price
// chart only loads when a user opens an entity that actually has chart data.
const EntityPriceChart = lazy(() => import('../components/EntityPriceChart'));
import { useAuth } from '../contexts/AuthContext';
import EntityAvatar from '../components/EntityAvatar';
import { isAiGeneratedPortrait } from '../lib/avatars';
import EntityComments from '../components/EntityComments';
import BackButton from '../components/BackButton';
import ShareButton from '../components/ShareButton';
import DeceasedBadge from '../components/DeceasedBadge';
import FeeWarning from '../components/FeeWarning';
import { freeFillsLeft } from '../lib/rosterMoveMath';
import DropPreview from '../components/DropPreview';

const ENTITY_TYPE_LABELS = {
  country_leader: 'Ruler', ceo: 'CEO', actor: 'Actor',
  athlete: 'Athlete', musician: 'Artist', influencer: 'Influencer',
};

// Points formatter for score chips — X-mention rows are worth ₣0.00001
// each, so daily rollups can sum below 0.01: show enough decimals that the
// value is never a misleading "+0.0000" (2026-07 user report).
const fmtPoints = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '0.00';
  if (Math.abs(n) >= 0.01) return n.toFixed(2);
  const s = n.toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
  return s === '0' || s === '-0' ? '<0.01' : s;
};

// Modal variant — same sub-cent awareness, up to 6 decimals for micro-awards.
const fmtF = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a === 0 || a >= 0.01) return n.toFixed(2);
  return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
};

const AVATAR_COLORS = {
  country_leader: '#5a2d5e', ceo: '#1e3a5f', actor: '#c97a2e',
  athlete: '#2d7d5e', musician: '#b8860b', influencer: '#c94070',
};

export default function EntityDetail({ entityId, onBack, teamData, contextTeam, selectedLeague, onUpgrade }) {
  const { user } = useAuth();
  const [entity, setEntity] = useState(null);
  const [scores, setScores] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [dropping, setDropping] = useState(false);
  // 2026-08-16 — teamData is a prop we can't refetch here, so mirror roster
  // membership locally after draft/drop actions (same trick as justDrafted).
  const [justDropped, setJustDropped] = useState(false);
  const [confirmDraftOpen, setConfirmDraftOpen] = useState(false);
  const [confirmDropOpen, setConfirmDropOpen] = useState(false);
  // iter_172 — flip the CTA to "On Your Team" immediately after a successful
  // draft (teamData is a prop we can't refetch here), so it isn't re-clickable.
  const [justDrafted, setJustDrafted] = useState(false);
  // iter_181 — remember what we just paid so Purchase Price doesn't show "—"
  // until a reload (teamData prop can't be refetched here).
  const [justDraftedPrice, setJustDraftedPrice] = useState(null);
  const [watching, setWatching] = useState(false);
  // "Why these points?" player-facing provenance modal state.
  const [prov, setProv] = useState(null);
  const [provLoading, setProvLoading] = useState(false);
  // v5.8.4 — single source of truth for which event_types are CURRENTLY
  // in the game. Backend derives this from DEFAULT_SCORING_RULES (or the
  // admin-overridden rules in the DB), so deprecated types like
  // `viral_moment`, `scandal`, `controversy` (dropped in v5.7.12 for
  // defamation risk) are auto-filtered out of the Scoring History feed
  // — without us having to maintain a hardcoded blacklist.
  const [activeEventTypes, setActiveEventTypes] = useState(null);

  const load = useCallback(async () => {
    try {
      // 2026-07 UX audit P0 — watchlist joins the parallel batch (was a
      // sequential 5th round-trip that delayed the Watch button state).
      const [entRes, scoresRes, priceRes, activeRes, wRes] = await Promise.all([
        entityAPI.get(entityId),
        api.get(`/entities/${entityId}/scores`).catch(() => ({ data: { scores: [] } })),
        api.get(`/entities/${entityId}/price-history`).catch(() => ({ data: { price_history: [] } })),
        api.get('/scoring/active-event-types').catch(() => ({ data: { active_event_types: null } })),
        api.get('/watchlist').catch((e) => {
          // Watchlist enrichment is best-effort; the Watch button just
          // stays as "Watch" if we can't tell whether the user is
          // already watching.
          console.warn('[EntityDetail] watchlist enrichment failed:', e?.message || e);
          return null;
        }),
      ]);
      setEntity(entRes.data.entity);
      setScores(scoresRes.data.scores || []);
      setPriceHistory(priceRes.data.price_history || []);
      // null sentinel = "couldn't fetch the canonical list, show everything"
      // (graceful degradation — better to show legacy events than nothing).
      setActiveEventTypes(activeRes.data.active_event_types
        ? new Set(activeRes.data.active_event_types)
        : null);
      if (wRes) {
        setWatching((wRes.data.watchlist || []).some(w => w.entity_id === entityId));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  // v7 (Gemini audit 2026-06, P2): memoize the price-history chart math so
  // the filter/map/sort/group doesn't re-run on every render (e.g. when the
  // watch/draft buttons toggle state). Recomputes only when the price data
  // or selected league changes.
  const chart = useMemo(() => {
    const startBoundary = selectedLeague?.start_date
      ? new Date(selectedLeague.start_date)
      : new Date(new Date().getFullYear(), 0, 1);
    const endBoundary = selectedLeague?.end_date
      ? new Date(selectedLeague.end_date)
      : null;
    const totalWeeks = selectedLeague?.weeks ||
      (endBoundary
        ? Math.max(1, Math.round((endBoundary - startBoundary) / (7 * 24 * 60 * 60 * 1000)))
        : 52);
    // Filter to in-range entries, compute week index from league start.
    const filtered = priceHistory
      .filter(p => {
        if (!p.date) return true;
        const d = new Date(p.date);
        if (d < startBoundary) return false;
        if (endBoundary && d > endBoundary) return false;
        return true;
      })
      .map(p => {
        if (!p.date) return { week: 0, price: p.price, _ts: 0 };
        const d = new Date(p.date);
        const weekIdx = Math.floor((d - startBoundary) / (7 * 24 * 60 * 60 * 1000)) + 1;
        return { week: weekIdx, price: p.price, _ts: d.getTime() };
      })
      .sort((a, b) => a._ts - b._ts);
    // Group by week → keep the LAST price of each week (weekly snapshot).
    const byWeek = new Map();
    for (const p of filtered) byWeek.set(p.week, p);
    const chartData = Array.from(byWeek.values()).sort((a, b) => a.week - b.week);
    // X-axis WINDOW: scale to the TRACKED data, not the full league season — a
    // 5-week series in a 520-week (10-yr sandbox) league must not cram into the
    // far-left pixels. Show first→last tracked week + a little headroom, clamped
    // to the league bounds; falls back to the season only when data is empty.
    const firstWeek = chartData.length ? chartData[0].week : 1;
    const lastWeek = chartData.length ? chartData[chartData.length - 1].week : totalWeeks;
    const xMin = Math.max(1, firstWeek);
    // Guarantee a ~4-week minimum visible span so one/few points aren't squashed
    // onto a single edge; never exceed the league length.
    const xMax = Math.min(totalWeeks, Math.max(lastWeek + 1, xMin + 4));
    const xDomain = [xMin, xMax];
    // Even ticks across the visible window (~5).
    const span = Math.max(1, xMax - xMin);
    const tickCount = Math.min(6, Math.max(2, span + 1));
    const tickStep = Math.max(1, Math.round(span / (tickCount - 1)));
    const ticks = [];
    for (let w = xMin; w <= xMax; w += tickStep) ticks.push(w);
    if (ticks[ticks.length - 1] !== xMax) ticks.push(xMax);
    const chartLabel = selectedLeague?.name || `${new Date().getFullYear()} Season`;
    // Y-axis domain: pad min/max so a FLAT price line (e.g. a person stuck at
    // ₣1.00) doesn't make recharts emit the same tick ("1, 1, 1, 1, 1") down
    // the whole axis (2026-06 fix). Guarantee a minimum visible span.
    const prices = chartData.map(p => Number(p.price) || 0);
    let yDomain = ['auto', 'auto'];
    if (prices.length) {
      const lo = Math.min(...prices);
      const hi = Math.max(...prices);
      const span = hi - lo;
      const pad = span < 0.01 ? Math.max(0.5, Math.abs(hi) * 0.1 || 0.5) : span * 0.15;
      yDomain = [Math.max(0, lo - pad), hi + pad];
    }
    return { totalWeeks, chartData, ticks, chartLabel, yDomain, xDomain };
  }, [priceHistory, selectedLeague]);

  // v5.7.41 — count this person-card open for the ad cadence.
  // We fire once per entityId mount, NOT once per render. The hook
  // itself is no-op for premium users.
  const { triggerAdOnPersonOpen } = useAds();
  useEffect(() => {
    if (!entityId) return;
    triggerAdOnPersonOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const handleDraft = async () => {
    if (!teamData?.id) { toast.error('No team found'); return; }
    setDrafting(true);
    try {
      const res = await teamAPI.draft({
        team_id: teamData.id,
        entity_id: entityId,
        // Build 4 — cross-league guard: server 409s on stale-league drift.
        league_id: selectedLeague?.id || teamData.league_id,
      });
      // Backend message includes the roster-move fee when the team is finalized.
      toast.success(res.data?.message || `Drafted ${entity.name}!`);
      setJustDrafted(true);
      setJustDropped(false);
      setJustDraftedPrice(entity?.pricing?.price ?? null);
      // let the rest of the app (Stage/roster) know to refresh.
      try { window.dispatchEvent(new CustomEvent('fws:team-changed')); } catch { /* ignore */ }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Draft failed');
    } finally { setDrafting(false); setConfirmDraftOpen(false); }
  };

  // 2026-08-16 (owner request) — drop a rostered person straight from their
  // detail page. Post-finalize this is a roster move (fee ladder applies);
  // pre-finalize it's a plain refund.
  const handleDrop = async () => {
    if (!teamData?.id) { toast.error('No team found'); return; }
    setDropping(true);
    try {
      const res = await teamAPI.remove({ team_id: teamData.id, entity_id: entityId });
      const d = res.data || {};
      let msg = `Dropped ${entity.name} — ${formatFranc(d.refund ?? 0)} refunded`;
      if (d.penalty > 0) msg += ` (− ${formatFranc(d.penalty)} roster-move fee)`;
      if (d.captain_reassigned) msg += '. Captaincy reassigned — review your captain!';
      toast.success(msg);
      setJustDrafted(false);
      setJustDropped(true);
      try { window.dispatchEvent(new CustomEvent('fws:team-changed')); } catch { /* ignore */ }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Drop failed');
    } finally { setDropping(false); setConfirmDropOpen(false); }
  };

  // "Why these points?" — open the player-facing provenance trace for a score row.
  const openProvenance = async (score) => {
    setProvLoading(true);
    setProv({ _row: score });
    // Always have the scoring math from the row itself as a baseline.
    const rowAward = {
      event_type: score.event_type, emoji: score.emoji,
      base_points: score.base_points, multiplier: score.multiplier,
      occurrence_count: score.occurrence_count,
      total_points: score.total_points, source: score.source, created_at: score.created_at,
    };
    try {
      const r = await api.get(`/entities/${entityId}/scores/${score.event_id || 'none'}/provenance`, {
        params: { event_type: score.event_type, day: score.day || (score.created_at || '').slice(0, 10) },
      });
      setProv({ ...r.data, award: r.data.award || rowAward, _row: score });
    } catch (e) {
      setProv({ has_source: false, _row: score, award: rowAward });
    }
    setProvLoading(false);
  };

  const isOnTeam = justDropped
    ? false
    : (teamData?.slots?.some(s => s.entity_id === entityId) || justDrafted);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="p-4">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <p className="text-center text-muted-foreground mt-8">Person not found</p>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="entity-detail">
      <div className="flex items-center justify-between">
        <BackButton onClick={onBack} />
        <ShareButton
          path={`/person/${entityId}`}
          title={`${entity.name} — Fantasy World Star`}
          text={`Check out ${entity.name}'s stats on Fantasy World Star`}
          testid="share-entity-btn"
        />
      </div>

      {/* Header Card */}
      <div className="bg-card border border-border/60 rounded-2xl p-5 mb-4">
        <div className="flex items-start gap-4">
          <EntityAvatar entity={entity} size={64} />
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{entity.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="secondary">{ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type}</Badge>
              <Badge variant="outline" className="text-[10px]">{entity.sub_type?.replace(/_/g, ' ')}</Badge>
              {entity.metadata?.death_date && (
                <DeceasedBadge deathDate={entity.metadata.death_date} />
              )}
              {isAiGeneratedPortrait(entity) && (
                <Badge
                  variant="outline"
                  className="text-[10px] gap-1 border-violet-400/50 text-violet-600 dark:text-violet-300"
                  data-testid="ai-portrait-badge"
                  title="This portrait was generated by AI and is not a real photograph of this person."
                >
                  <Sparkles className="h-2.5 w-2.5" /> AI portrait
                </Badge>
              )}
            </div>
            {entity.metadata?.country && (
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {entity.metadata.country}
              </p>
            )}
            {entity.metadata?.team && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Star className="h-3 w-3" /> {entity.metadata.team}
              </p>
            )}
            {entity.metadata?.description && (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                {entity.metadata.description}
              </p>
            )}
            {/* v5.7.72 — Wikipedia photo credit removed (owner request).
                The Wikipedia backup photo is no longer displayed, so the
                credit was always misleading — most visibly on deceased
                people, who render the DiceBear fallback yet still carried
                the stale attribution. No displayed image needs Commons
                attribution anymore. */}
          </div>
        </div>

        {/* Actions — 2026-08-16 (owner request): finalized rosters are fully
            editable from here. Draft into an empty slot (fee-aware confirm)
            or DROP a rostered person directly. The old "Roster locked · use
            Transfers" dead end is gone (the Transfers screen no longer exists). */}
        <div className="mt-4 flex gap-2 flex-wrap">
          {isOnTeam ? (
            <>
              <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20 text-sm px-4 py-1.5" data-testid="entity-detail-on-team">
                {entity.metadata?.death_date ? 'On Your Team · RIP' : 'On Your Team'}
              </Badge>
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDropOpen(true)}
                disabled={dropping}
                data-testid="entity-detail-drop"
              >
                <Minus className="h-4 w-4 mr-2" /> {dropping ? 'Dropping...' : 'Drop from Team'}
              </Button>
            </>
          ) : entity.metadata?.death_date ? (
            <Badge
              variant="outline"
              className="text-sm px-4 py-1.5 text-muted-foreground"
              data-testid="entity-detail-undraftable"
              title={`Deceased ${entity.metadata.death_date} — no longer draftable.`}
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" /> No longer draftable
            </Badge>
          ) : (() => {
            // 2026-06 audit — grey out when unaffordable (parity with Draft
            // Room's "No ₣" rows) instead of letting the 400 toast do the job.
            const price = entity.pricing?.price || 0;
            const cantAfford = teamData && !teamData.finalized && price > (teamData.budget_remaining || 0);
            return (
              <Button
                onClick={() => (teamData?.finalized ? setConfirmDraftOpen(true) : handleDraft())}
                disabled={drafting || cantAfford}
                title={cantAfford ? `Needs ${formatFranc(price)} — you have ${formatFranc(teamData.budget_remaining || 0)}` : undefined}
                data-testid="entity-detail-draft"
              >
                <Plus className="h-4 w-4 mr-2" /> {drafting ? 'Drafting...' : cantAfford ? `No ₣ — needs ${formatFranc(price)}` : `Draft for ${formatFranc(price)}`}
              </Button>
            );
          })()}
          <Button
            variant={watching ? 'default' : 'outline'}
            onClick={async () => {
              try {
                if (watching) {
                  await api.delete(`/watchlist/${entityId}`);
                  setWatching(false);
                  toast.success('Removed from watchlist');
                } else {
                  await api.post(`/watchlist/${entityId}`);
                  setWatching(true);
                  toast.success('Added to watchlist');
                }
                // v5.7.91 — Broadcast so the Watchlist page (which stays
                // mounted under this overlay in the nav stack) refreshes
                // its list when the user backs out. Without this it was
                // showing stale rows for an unwatched person until the
                // user fully left and re-entered the watchlist tab.
                window.dispatchEvent(new CustomEvent('watchlist:changed', { detail: { entityId } }));
              } catch (e) { toast.error('Failed'); }
            }}
            data-testid="entity-detail-watch"
          >
            {watching ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {watching ? 'Watching' : 'Watch'}
          </Button>
        </div>
      </div>

      {/* Post-finalize draft confirm — mirrors the Draft Room's dialog */}
      <AlertDialog open={confirmDraftOpen} onOpenChange={setConfirmDraftOpen}>
        <AlertDialogContent data-testid="entity-detail-draft-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Add to your finalized team?</AlertDialogTitle>
            <AlertDialogDescription>
              {freeFillsLeft(teamData) > 0
                ? <>Adding {entity.name} fills one of your open slots for the market price ({formatFranc(entity.pricing?.price)}) — no roster-move fee, and it doesn't count toward this week's moves.</>
                : <>Adding {entity.name} counts as a roster move: you pay the market price
                  ({formatFranc(entity.pricing?.price)}) plus a roster-move fee — your first
                  move each week is free, then 2%, 5% and up of your remaining budget.</>}
            </AlertDialogDescription>
            <FeeWarning team={teamData} />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entity-detail-draft-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDraft} disabled={drafting} data-testid="entity-detail-draft-confirm-btn">
              {drafting ? 'Adding…' : 'Add to Team'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drop confirm */}
      <AlertDialog open={confirmDropOpen} onOpenChange={setConfirmDropOpen}>
        <AlertDialogContent data-testid="entity-detail-drop-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Drop {entity.name} from your team?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll be refunded their current market value.
              {teamData?.finalized
                ? ' Dropping is free — it won\'t count as a roster move; only adding a replacement does. If they are your captain, captaincy moves to your highest-value player.'
                : ''}
            </AlertDialogDescription>
            {confirmDropOpen && teamData?.id && <DropPreview teamId={teamData.id} entityId={entity.id} />}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entity-detail-drop-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDrop}
              disabled={dropping}
              data-testid="entity-detail-drop-confirm-btn"
            >
              {dropping ? 'Dropping…' : 'Drop'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v5.7.83 — Official link chips. Sources from the metadata fields
          populated by Master Deep Resolve (v5.7.82). Each chip is a
          one-tap external link; we open in a new tab + `rel=noopener
          noreferrer` so the linked site can't navigate back into our
          window via window.opener. Chips only render when the value is
          present so unresolved entities don't show empty rows. */}
      {(() => {
        const m = entity.metadata || {};
        const links = [
          m.x_handle           && { href: `https://x.com/${m.x_handle}`,           label: `@${m.x_handle}`,    icon: '𝕏',    testid: 'profile-link-x' },
          m.youtube_channel_url && { href: m.youtube_channel_url,                  label: 'YouTube',           icon: '▶',    testid: 'profile-link-youtube' },
          m.instagram_handle   && { href: `https://instagram.com/${m.instagram_handle}`, label: `@${m.instagram_handle}`, icon: '📷', testid: 'profile-link-instagram' },
          m.tiktok_handle      && { href: `https://www.tiktok.com/@${m.tiktok_handle}`, label: `@${m.tiktok_handle}`, icon: '🎵', testid: 'profile-link-tiktok' },
          m.twitch_handle      && { href: `https://www.twitch.tv/${m.twitch_handle}`, label: m.twitch_handle,   icon: '🎮',   testid: 'profile-link-twitch' },
          m.facebook_handle    && { href: `https://www.facebook.com/${m.facebook_handle}`, label: 'Facebook',     icon: 'ⓕ',    testid: 'profile-link-facebook' },
          m.official_website   && { href: m.official_website,                     label: 'Website',           icon: '🔗',   testid: 'profile-link-website' },
        ].filter(Boolean);
        if (!links.length) return null;
        return (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="profile-link-row">
            {links.map((l) => (
              <a
                key={l.testid}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium hover:bg-secondary/80 transition-colors active:scale-95"
                data-testid={l.testid}
              >
                <span aria-hidden className="text-sm leading-none">{l.icon}</span>
                <span className="truncate max-w-[140px]">{l.label}</span>
              </a>
            ))}
          </div>
        );
      })()}

      {/* Stats Grid */}
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" data-testid="performance-heading">
        <BarChart3 className="h-4 w-4" /> Performance
      </h3>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-card border border-border/60 rounded-xl p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><FrancIcon className="h-3 w-3" />Current Price</p>
          <p className="text-xl font-bold tabular-nums">{formatFranc(entity.pricing?.price)}</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Expected ₣</p>
          <p className="text-xl font-bold tabular-nums">{formatFranc(entity.pricing?.expected_points)}</p>
        </div>
        {/* 2026-07-21 (owner request) — "Form Score" (a static internal
            stat nobody understood) replaced with the viewer's Purchase
            Price + gain/loss vs Current Price. Shows "—" when the person
            isn't on the viewer's team.
            2026-08-12 (owner request) — when drilled from an OPPONENT's team
            (contextTeam), show what THAT manager paid ("Bought For") instead. */}
        {(() => {
          const _ctx = contextTeam || teamData;
          const _isOpp = !!contextTeam;
          const _slotAcq = _ctx?.slots?.find((s) => s.entity_id === entityId)?.acquired_price;
          // iter_181 — mirror in-page draft/drop so the card is live without a reload.
          const acq = _isOpp ? _slotAcq : (justDropped ? null : (_slotAcq ?? justDraftedPrice));
          return (
            <div className="bg-card border border-border/60 rounded-xl p-3" data-testid="purchase-price-card">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> {_isOpp ? 'Bought For' : 'Purchase Price'}
              </p>
              {acq == null ? (
                <p className="text-xl font-bold tabular-nums text-muted-foreground"
                   title={_isOpp ? 'Not on this team' : 'Not on your team'}>—</p>
              ) : (() => {
                const delta = Math.round(((entity.pricing?.price || 0) - acq) * 100) / 100;
                return (
                  <p className="text-xl font-bold tabular-nums">
                    {formatFranc(acq)}{' '}
                    <span
                      className={`text-xs font-semibold ${delta >= 0 ? 'text-[hsl(var(--positive-text))]' : 'text-destructive'}`}
                      data-testid="purchase-price-delta"
                    >
                      {delta >= 0 ? '▲' : '▼'}{formatFranc(Math.abs(delta))}
                    </span>
                  </p>
                );
              })()}
            </div>
          );
        })()}
        <div className="bg-card border border-border/60 rounded-xl p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Ownership</p>
          <p className="text-xl font-bold tabular-nums">{entity.stats?.ownership_percentage?.toFixed(1)}%</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-3">
          <p className="text-xs text-muted-foreground">Total ₣ Earned</p>
          <p className="text-xl font-bold tabular-nums">{formatFranc(entity.stats?.total_points)}</p>
        </div>
        <div className="bg-card border border-border/60 rounded-xl p-3">
          <p className="text-xs text-muted-foreground">Weekly ₣</p>
          <p className="text-xl font-bold tabular-nums">{formatFranc(entity.stats?.weekly_points)}</p>
        </div>
      </div>

      {/* Price History Chart — weekly snapshot of the entity's price.
          v5.7.54: x-axis explicitly labelled as Week, tooltip + axis
          ticks read "W1, W2, …" so it's clear the x is the week
          counter, not an arbitrary index.
          v5.7.63: x-axis now syncs to the selected league's lifecycle.
          • If a league is selected with a `start_date`, points are
            filtered to entries on/after that date and weeks are numbered
            from league start (W1 = league week 1).
          • If no league is selected, the axis defaults to Jan 1 of the
            current year so the chart is bounded to "this season".
          v5.7.68: TWO bugs fixed.
          • Backend was appending 3+ historical_price entries within
            seconds (intended as a weekly snapshot but firing on every
            scoring tick). Same-week points all rendered at the same
            x position and produced "W13, W19, W19, W19" overlap on the
            axis. Now grouped by week — keep the LAST price of each
            week so "weekly snapshot" is what the chart actually shows.
          • XAxis switched to `type="number"` with an explicit domain
            from league W1 → current week — Recharts now treats the
            week counter as a continuous scale and picks non-colliding
            ticks. Even if a player only has data from W13-W19, the
            axis shows W1 → W52 so the user sees the league-season
            context, not a misleading "6-week" tunnel. */}
      {(() => {
        const { totalWeeks, chartData, ticks, chartLabel, yDomain, xDomain } = chart;
        return chartData.length >= 1 && (
          <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="price-history-chart">
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Price History <span className="text-[10px] text-muted-foreground font-normal">(weekly)</span>
            </h3>
            <p className="text-[10px] text-muted-foreground mb-3" data-testid="price-history-league-label">
              {chartLabel} · {chartData.length} of {totalWeeks} weeks tracked
            </p>
            <Suspense fallback={<Skeleton className="h-[170px] w-full rounded-xl" />}>
              <EntityPriceChart chartData={chartData} totalWeeks={totalWeeks} ticks={ticks} yDomain={yDomain} xDomain={xDomain} />
            </Suspense>
          </div>
        );
      })()}

      {/* Premium Analytics — removed in v5.4.0. Premium is now ad-free only. */}

      {!user?.premium && (
        <div
          className="bg-secondary/30 border border-border/40 rounded-xl p-3 mb-4 flex items-center gap-3"
          data-testid="entity-premium-upsell-banner"
        >
          <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium">Tired of popup ads?</p>
            <p className="text-[10px] text-muted-foreground">Go ad-free for life — $4.99 one-time. No gameplay advantage.</p>
          </div>
          <Button
            size="sm"
            className="shrink-0"
            onClick={() => onUpgrade && onUpgrade()}
            data-testid="entity-premium-upsell-btn"
          >
            <Crown className="h-3.5 w-3.5 mr-1.5" /> Go ad-free
          </Button>
        </div>
      )}

      {/* In the News (Task 3) — scored events rendered as oval chips so
          users can see WHY this person earned ₣. Each oval shows the
          event emoji, a short label, the points awarded, and the date.
          v5.8.4 filter: only show event_types that are CURRENTLY in the
          active rules (deprecated/defamatory types auto-drop). null =
          couldn't fetch the canonical list → show everything. */}
      <div className="bg-card border border-border/60 rounded-2xl p-4" data-testid="entity-news-section">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Newspaper className="h-4 w-4" /> In the News
        </h3>
        {(() => {
          const visibleScores = activeEventTypes
            ? scores.filter(s => activeEventTypes.has(s.event_type))
            : scores;
          if (visibleScores.length === 0) {
            return <p className="text-sm text-muted-foreground text-center py-4">No scored news events yet</p>;
          }
          return (
            <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto pr-1" data-testid="entity-scoring-history">
              {visibleScores.map((score) => (
                <div
                  key={score.id}
                  className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full border border-border/50 bg-secondary/40"
                  data-testid="entity-news-oval"
                  title={score.created_at ? new Date(score.created_at).toLocaleString() : ''}
                >
                  {/* v5.7.55 — event-type emoji from backend EVENT_EMOJIS map. */}
                  <span aria-hidden className="text-base leading-none" data-testid={`score-emoji-${score.event_type || 'event'}`}>{score.emoji || '📰'}</span>
                  <span className="text-xs font-medium capitalize">{score.event_type?.replace(/_/g, ' ')}</span>
                  {score.occurrence_count > 1 && (
                    <span className="text-[10px] font-semibold text-muted-foreground" data-testid="score-occurrence-count">×{score.occurrence_count}</span>
                  )}
                  <span className={`text-xs font-bold tabular-nums ${Number(score.total_points) < 0 ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--positive-text))]'}`}>{Number(score.total_points) < 0 ? '' : '+'}{fmtPoints(score.total_points)}</span>
                  {score.multiplier > 1 && (
                    <span className="text-[9px] text-muted-foreground">{score.multiplier}x</span>
                  )}
                  {(score.day || score.created_at) && (
                    // X rollup rows carry `day` = their UTC bucket; render THAT
                    // date (noon-UTC anchor) so two adjacent UTC-day buckets
                    // never both display the same local date.
                    <span className="text-[9px] text-muted-foreground">· {score.day ? new Date(`${score.day}T12:00:00Z`).toLocaleDateString() : new Date(score.created_at).toLocaleDateString()}</span>
                  )}
                  {score.source_url && (
                    <a
                      href={score.source_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-foreground"
                      title={`Source: ${score.source_name || 'article'}`}
                      data-testid="score-source-link"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openProvenance(score); }}
                    className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                    title="Why these points?"
                    data-testid="why-points-btn"
                  >
                    <HelpCircle className="h-3 w-3" /> Why?
                  </button>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Entity Discussion */}
      <div className="mt-4">
        <EntityComments entityId={entityId} entityName={entity.name} />
      </div>

      {/* v6.x (Task 3) — unified affiliation/AI/no-prizes disclaimer + contact line
          (owner-finalized 2026-06, shared with Explore + mobile via AFFILIATION_DISCLAIMER). */}
      <div className="mt-5 mb-2">
        <AffiliationDisclaimer testId="entity-affiliation-disclaimer" />
      </div>

      {/* "Why these points?" — player-facing provenance trace */}
      <Dialog open={!!prov} onOpenChange={(o) => !o && setProv(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="why-points-modal">
          <DialogHeader>
            <DialogTitle>Why these points?</DialogTitle>
            <DialogDescription>How this score was calculated — fully traceable to a public event.</DialogDescription>
          </DialogHeader>
          {provLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : prov && (
            <div className="space-y-3 text-sm" data-testid="why-points-body">
              {prov.award && (
                <div className="rounded-xl bg-secondary/40 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span aria-hidden className="text-lg leading-none">{prov.award.emoji || '📰'}</span>
                    <span className="font-medium capitalize">{(prov.award.event_type || 'event').replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {prov.award.base_points != null && prov.award.multiplier != null ? (
                      <>Base ₣{fmtF(prov.award.base_points)} × {prov.award.multiplier}x{Number(prov.award.occurrence_count) > 1 ? ` × ${Number(prov.award.occurrence_count).toLocaleString()}` : ''} ={' '}</>
                    ) : null}
                    <span className="font-bold text-[hsl(var(--positive-text))]">₣{fmtF(prov.award.total_points)}</span>
                    {prov.award.base_points == null && Number(prov.award.occurrence_count) > 1 ? (
                      <span className="ml-1">· ×{Number(prov.award.occurrence_count).toLocaleString()} {String(prov.award.event_type || '').startsWith('x_') ? 'mentions' : 'times'}</span>
                    ) : null}
                  </div>
                  {prov.award.created_at && (
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(prov.award.created_at).toLocaleString()}</p>
                  )}
                </div>
              )}
              {Array.isArray(prov.awards) && prov.awards.filter(a => a.event_type !== prov.award?.event_type).length > 0 && (
                <div className="rounded-xl bg-secondary/20 p-2" data-testid="why-points-siblings">
                  <p className="text-[10px] text-muted-foreground mb-1">Also scored from this article:</p>
                  {prov.awards.filter(a => a.event_type !== prov.award?.event_type).map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-0.5">
                      <span className="capitalize">{a.emoji} {(a.event_type || '').replace(/_/g, ' ')}</span>
                      <span className="tabular-nums text-[hsl(var(--positive-text))]">+₣{fmtF(a.total_points)}</span>
                    </div>
                  ))}
                </div>
              )}
              {prov.summary && <p className="text-sm">{prov.summary}</p>}
              {prov.has_source && prov.source_url ? (
                <a
                  href={prov.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 text-xs inline-flex items-center gap-1"
                  data-testid="why-points-source-link"
                >
                  <ExternalLink className="h-3 w-3" /> Read the source{prov.source_name ? ` · ${prov.source_name}` : ''}
                </a>
              ) : (
                <p className="text-xs text-muted-foreground">
                  This is attention-driven income from real-world social mentions — there's no single
                  article to link, but every ₣ tracks public activity.
                </p>
              )}
              <p className="text-[10px] text-muted-foreground border-t border-border/40 pt-2">
                Scores come only from objective, verifiable public events — never opinions or ratings of character.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
